import { Loader2, Send } from 'lucide-react';
import WhatsAppStatusCheck from './WhatsAppStatusCheck';

// Extraído de WhatsAppPanel.jsx (02.3-client.md paso b) -- historial de mensajes + composer,
// mostrado cuando hay un `activeChat`. Estilos inline verbatim; ningún cambio de comportamiento
// (la burbuja "enviando..." se sigue derivando en el container desde `mutation.isPending`, este
// componente solo la renderiza como un mensaje más del arreglo `messages`).
export default function WhatsAppChatThread({
  messages, loadingMsgs, inputText, onInputChange, onSend, sendingMsg, messagesEndRef,
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#0e0e10' }}>
      {/* Scrollable conversation bubble area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-4) var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', minHeight: '350px', maxHeight: 'calc(100vh - 120px)' }}>
        {loadingMsgs ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 'var(--space-2)' }}>
            <Loader2 size={20} style={{ color: '#f97316', animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>Cargando historial...</span>
          </div>
        ) : messages.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'rgba(255,255,255,0.3)', fontSize: '12px', fontStyle: 'italic', padding: 'var(--space-12) 0' }}>
            No hay mensajes. Envía un mensaje para comenzar la conversación.
          </div>
        ) : (
          <>
            {messages.map((msg) => {
              const fromMe = msg.from_me;
              return (
                <div
                  key={msg.msg_id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignSelf: fromMe ? 'flex-end' : 'flex-start',
                    maxWidth: '85%'
                  }}
                >
                  <div style={{
                    background: fromMe ? '#0b663b' : '#202023',
                    color: '#fff',
                    borderRadius: 'var(--radius-lg)',
                    borderTopRightRadius: fromMe ? '2px' : '10px',
                    borderTopLeftRadius: !fromMe ? '2px' : '10px',
                    padding: 'var(--space-2) var(--space-3)',
                    fontSize: '12.5px',
                    lineHeight: '1.4',
                    position: 'relative',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
                  }}>
                    {/* 1. Image Preview */}
                    {msg.media_url && msg.media_type === 'image' && (
                      <div style={{ marginBottom: 'var(--space-2)', borderRadius: 'var(--radius-md)', overflow: 'hidden', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <img
                          src={msg.media_url}
                          alt="WhatsApp"
                          onClick={() => window.open(msg.media_url, '_blank')}
                          style={{
                            width: '100%',
                            maxHeight: '200px',
                            objectFit: 'cover',
                            display: 'block',
                            transition: 'transform 0.2s'
                          }}
                          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
                          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                        />
                      </div>
                    )}

                    {/* 2. Video Preview with controls */}
                    {msg.media_url && msg.media_type === 'video' && (
                      <div style={{ position: 'relative', marginBottom: 'var(--space-2)', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)', background: '#000' }}>
                        <video
                          src={msg.media_url}
                          controls
                          preload="metadata"
                          style={{
                            width: '100%',
                            maxHeight: '220px',
                            display: 'block'
                          }}
                        />
                        <button
                          onClick={(e) => { e.stopPropagation(); window.open(msg.media_url, '_blank'); }}
                          title="Expandir en pestaña nueva"
                          aria-label="Expandir en pestaña nueva"
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: 'rgba(0, 0, 0, 0.6)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            color: '#fff',
                            padding: 'var(--space-1)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            zIndex: 15,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)'}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                        </button>
                      </div>
                    )}

                    {/* 3. Document / PDF Preview Card */}
                    {msg.media_url && msg.media_type === 'document' && (
                      <div
                        onClick={() => window.open(msg.media_url, '_blank')}
                        style={{
                          marginBottom: 'var(--space-2)',
                          background: 'rgba(0, 0, 0, 0.2)',
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          borderRadius: 'var(--radius-md)',
                          padding: 'var(--space-3) var(--space-3)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-3)',
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.35)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.2)'}
                      >
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: 'var(--radius-md)',
                          background: 'rgba(239, 68, 68, 0.1)',
                          color: '#ef4444',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#f3f4f6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            Documento PDF / Archivo
                          </div>
                          <div style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.4)' }}>
                            Haga clic para abrir o descargar
                          </div>
                        </div>
                        <div style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                        </div>
                      </div>
                    )}

                    {/* 4. Text caption content */}
                    {msg.text_content && msg.text_content !== '📷 Imagen' && msg.text_content !== '🎥 Video' && msg.text_content !== '📄 Documento' && (
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {msg.text_content}
                      </div>
                    )}

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 'var(--space-1)',
                      marginTop: 'var(--space-1)',
                      fontSize: '9px',
                      color: 'rgba(255,255,255,0.4)',
                      textAlign: 'right',
                      lineHeight: 1
                    }}>
                      <span>{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
                      {fromMe && <WhatsAppStatusCheck status={msg.status} />}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input bar at the bottom */}
      <form
        onSubmit={onSend}
        style={{
          padding: 'var(--space-3) var(--space-3)',
          background: '#1c1c1e',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          position: 'sticky',
          bottom: 0,
          zIndex: 10
        }}
      >
        <input
          type="text"
          placeholder="Escribe un mensaje..."
          aria-label="Escribe un mensaje"
          value={inputText}
          onChange={e => onInputChange(e.target.value)}
          disabled={sendingMsg}
          style={{
            flex: 1,
            background: '#0e0e10',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--space-2) var(--space-4)',
            fontSize: '12.5px',
            color: '#fff',
            outline: 'none',
            transition: 'border-color .2s'
          }}
          onFocus={e => e.currentTarget.style.borderColor = 'rgba(249, 115, 22, 0.5)'}
          onBlur={e => e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.06)'}
        />
        <button
          type="submit"
          aria-label="Enviar mensaje"
          disabled={!inputText.trim() || sendingMsg}
          style={{
            background: '#f97316',
            color: '#fff',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'background .2s, opacity .2s',
            opacity: (!inputText.trim() || sendingMsg) ? 0.5 : 1
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#ea580c'}
          onMouseLeave={e => e.currentTarget.style.background = '#f97316'}
        >
          <Send size={14} />
        </button>
      </form>
    </div>
  );
}
